import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    updateDoc,
    where,
    getAggregateFromServer,
    count
} from "firebase/firestore";
import { db, storage } from "../../../firebase";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { SendPageView } from "../../../func/analyticsFunctions";
import ProfilePicture, { useProfilePicturesProvider } from "../../util/ProfilePicture";
import confirmPopup from "../../util/ConfirmPopup";
import Spinner from "../../util/Spinner";
import EditProfileForm from "./EditProfileForm";
import { ArrowBackIcon, CopyIcon } from "../../util/Icons";
import { SubMenuItem } from "../../util/SubMenu";
import SubMenuWrapper from "../../util/SubMenu";
import SinglePost from "../SocialFeed/SinglePost";
import { usePopUpContext } from "../../../context/PopUpProvider";
import DisplayName from "../../util/DisplayName";

/**
 * SocialProfilePage - Displays a user's public profile
 * - Profile data derived from social_feed posts (authorName from most recent post)
 * - Profile picture from Firebase Storage via ProfilePicture component
 * - Optional bio/location from social_profile/{uid}
 */
const SocialProfilePage = () => {
    const { uid } = useParams();
    const { user, userDetails, FOUNDER_UIDS } = useUserContext();
    const { getURL, setPicturesMap } = useProfilePicturesProvider();
    const [status, setStatus] = useState("loading"); // loading | notFound | ok
    const [displayName, setDisplayName] = useState("");
    const [posts, setPosts] = useState([]);
    const [profileData, setProfileData] = useState(null); // bio, location from social_profile
    const [lastPost, setLastPost] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMorePosts, setHasMorePosts] = useState(true);
    const [profilePicUrl, setProfilePicUrl] = useState(null);
    const [totalUserPosts, setTotalUserPosts] = useState(null);
    const postLimit = 5;

    const isOwner = user?.uid === uid;

    const { showPopUp } = usePopUpContext()

    // Fetch user posts and profile data
    useEffect(() => {
        if (!uid) return;

        async function fetchProfileData() {
            setStatus("loading");
            setPosts([]);
            setLastPost(null);
            setHasMorePosts(true);

            try {
                // Get total number of posts
                const q = query(collection(db, "social_feed"), where("authorID", "==", uid))
                const newPostsTotalSnap = await getAggregateFromServer(q, { totalCount: count() })
                setTotalUserPosts(newPostsTotalSnap.data().totalCount)

                // Fetch optional social_profile data
                const profileDoc = await getDoc(doc(db, "social_profile", uid));
                const nextProfileData = profileDoc.exists() ? profileDoc.data() : null;
                setProfileData(nextProfileData);

                // Fetch posts by authorID
                console.log("Fetching profile for uid:", uid);
                const postsQuery = query(
                    collection(db, "social_feed"),
                    where("authorID", "==", uid),
                    orderBy("epochTime", "desc"),
                    limit(postLimit)
                );
                const postsSnap = await getDocs(postsQuery);
                console.log("Posts query result - size:", postsSnap.size, "empty:", postsSnap.empty);

                if (postsSnap.empty) {
                    console.log("No posts found for uid:", uid);
                    if (isOwner) {
                        setDisplayName(getOwnerDisplayName(userDetails, user));
                        setPosts([]);
                        setLastPost(null);
                        setHasMorePosts(false);
                        setStatus("ok");
                    } else {
                        setStatus("notFound");
                    }
                    return;
                }

                const postsData = [];
                postsSnap.forEach((doc) => {
                    postsData.push({ id: doc.id, ...doc.data() });
                });

                // Derive displayName from most recent post
                setDisplayName(postsData[0]?.authorName || "Unknown User");
                setPosts(postsData);
                setLastPost(postsSnap.docs[postsSnap.docs.length - 1]);
                setHasMorePosts(postsSnap.size === postLimit);

                setStatus("ok");
            } catch (err) {
                console.error("Error fetching profile:", err.message);
                setStatus("notFound");
            }
        }

        fetchProfileData();
    }, [uid, isOwner, user, userDetails]);

    // Scroll to top when navigating to this page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [uid]);

    // Set dynamic page title
    useEffect(() => {
        if (displayName && displayName !== "Unknown User") {
            const title = `${displayName}'s Profile - MCC`;
            document.title = title;
            SendPageView(title);
        }
    }, [displayName]);

    // Load more posts
    async function loadMorePosts() {
        if (!lastPost || loadingMore || !hasMorePosts) return;
        setLoadingMore(true);

        try {
            const morePostsQuery = query(
                collection(db, "social_feed"),
                where("authorID", "==", uid),
                orderBy("epochTime", "desc"),
                startAfter(lastPost),
                limit(postLimit)
            );
            const postsSnap = await getDocs(morePostsQuery);

            if (postsSnap.empty) {
                setHasMorePosts(false);
            } else {
                const newPosts = [];
                postsSnap.forEach((doc) => {
                    newPosts.push({ id: doc.id, ...doc.data() });
                });
                setPosts((prev) => [...prev, ...newPosts]);
                setLastPost(postsSnap.docs[postsSnap.docs.length - 1]);
                setHasMorePosts(postsSnap.size === postLimit);
            }
        } catch (err) {
            console.error("Error loading more posts:", err.message);
        }

        setLoadingMore(false);
    }

    // Handle profile save from EditProfileForm
    function handleProfileSave(newProfileData) {
        setProfileData(newProfileData);
    }

    async function deleteProfilePost(post) {
        let confirm = await confirmPopup({ message: "Delete this post?", textY: "Delete", textN: "Nevermind" });
        if (!confirm) return;

        const postID = post?.id;
        if (!postID) return;

        if (post?.picturesRefs?.length > 0) {
            for (const picturePath of post.picturesRefs) {
                try {
                    await deleteObject(ref(storage, picturePath));
                } catch (err) {
                    console.error("Error deleting post picture:", err.message);
                }
            }
        }

        try {
            await deleteDoc(doc(db, "social_feed", postID));
            setPosts((prev) => prev.filter((item) => item.id !== postID));
            setTotalUserPosts((prev) => {
                const nextCount = Math.max((prev || 0) - 1, 0);
                if (nextCount === 0) {
                    setStatus("notFound");
                }
                return nextCount;
            });
        } catch (err) {
            console.error("Error deleting post:", err.message);
        }
    }

    async function editProfilePost(post, message) {
        const postID = post?.id;
        if (!postID) return;
        if (user.uid !== post?.authorID) return;

        const editTime = Date.now();
        const postPatch = {
            message,
            editedAt: editTime,
            updatedAt: new Date(editTime).toISOString(),
        };

        await updateDoc(doc(db, "social_feed", postID), postPatch);
        setPosts((prev) => {
            return prev.map((item) => {
                if (item.id !== postID) return item;
                return { ...item, ...postPatch };
            });
        });

        return postPatch;
    }

    // Upload profile picture (reused from AccountPage)
    async function uploadProfilePicture(e) {
        const picture = e?.target?.files[0] || null;
        if (!picture) return;
        console.log("Uploading profile picture");
        let picPath = `profile_pictures/${uid}`;
        let picRef = ref(storage, picPath);
        await uploadBytes(picRef, picture);
        console.log("Upload complete");
        const downloadURL = await getDownloadURL(picRef);
        setPicturesMap(uid, downloadURL);
        setProfilePicUrl(downloadURL);
    }

    // Delete profile picture (reused from AccountPage)
    async function deleteProfilePicture() {
        let confirm = await confirmPopup({ textY: "Yes", textN: "No" });
        if (!confirm) return;
        console.log("Deleting profile picture");
        await deleteObject(ref(storage, `/profile_pictures/${uid}`))
            .then(() => {
                console.log("Delete success");
                setPicturesMap(uid, null);
                setProfilePicUrl(null);
            })
            .catch((err) => {
                console.error(err.message);
            });
    }


    return (
        <div className="bg-white min-h-screen">
            <SubMenuWrapper >
                <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Home"} to={"/"} />
            </SubMenuWrapper>

            {/* Loading State */}
            {status === "loading" &&
                <div className="flex justify-center items-center py-20">
                    <Spinner size={64} />
                </div>
            }

            {/* notFound */}
            {status === "notFound" &&
                <div className="max-w-2xl mx-auto py-10 px-4 text-center">
                    <div className="bg-white rounded-xl shadow-md p-8">
                        <h2 className="text-xl font-semibold text-slate-800 mb-2">Profile not available yet</h2>
                        <p className="text-slate-500 mb-4">
                            This member hasn't shared a public profile yet.
                        </p>
                        <Link
                            to="/"
                            className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors no-underline"
                        >
                            Go to Feed
                        </Link>
                    </div>
                </div>
            }

            {/* Ok - Render profile*/}
            {status === "ok" &&
                <div className="max-w-2xl mx-auto py-4 px-2">
                    {/* Profile Header */}
                    <div className="bg-white rounded-xl shadow-md p-6 mb-4">
                        <div className="flex items-start gap-4">
                            {/* Profile Picture - Clickable */}
                            <div className="flex-shrink-0">
                                <div
                                    className="cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={async () => {
                                        const url = await getURL(uid);
                                        if (url) {
                                            setProfilePicUrl(url);
                                            // setShowProfilePicModal(true);
                                            showPopUp(<LightBoxModal src={url} alt={`${displayName}'s profile`} />)
                                        }
                                    }}
                                >
                                    <ProfilePicture
                                        userID={uid}
                                        name={displayName}
                                        className=" !w-20 !h-20 text-2xl"
                                        refreshToken={profilePicUrl}
                                    />
                                </div>
                                {/* Profile Picture Upload/Delete for Owner */}
                                {isOwner && (
                                    <div className="mt-2 flex flex-col gap-1">
                                        <label
                                            htmlFor="profilePictureInput"
                                            className="text-xs text-center text-blue-600 hover:text-blue-700 cursor-pointer select-none"
                                        >
                                            Change Photo
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="profilePictureInput"
                                                style={{ display: "none" }}
                                                onInput={uploadProfilePicture}
                                            />
                                        </label>
                                        {profilePicUrl && (
                                            <button
                                                onClick={deleteProfilePicture}
                                                className="text-xs text-red-500 hover:text-red-600 cursor-pointer select-none bg-transparent border-none p-0"
                                            >
                                                Delete Photo
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Profile Info */}
                            <div className="flex-1 min-w-0">
                                {/* <h1 className="text-xl font-bold text-slate-800 mb-1">{displayName}</h1> */}
                                <DisplayName uid={uid} fullName={displayName} className="text-xl font bold text-slate-800" />
                                {FOUNDER_UIDS.includes(uid) && <div className="flex items-center gap-1 mb-1">
                                    <p className="m-0 inline-block px-2 py-0.5 bg-gray-200 text-gray-600 text-[10px] rounded-full border-1 border-yellow-600">Founding Member</p>
                                </div>
                                }

                                {/* Bio & Location */}
                                {profileData?.location && (
                                    <p className="text-sm text-slate-500 mb-1">📍 {profileData.location}</p>
                                )}
                                {profileData?.bio && (
                                    <p className="text-slate-600 mb-0 whitespace-pre-line">{profileData.bio}</p>
                                )}

                                {/* Empty state for non-owner when no bio/location */}
                                {!profileData?.bio && !profileData?.location && !isOwner && (
                                    <p className="text-slate-400 text-sm italic mb-0">No bio yet</p>
                                )}
                                {!profileData?.bio && !profileData?.location && isOwner && (
                                    <p className="text-slate-500 text-sm mb-0">
                                        Add a location and short bio to help other members recognize you.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Edit Form for Owner */}
                        {isOwner && (
                            <EditProfileForm
                                uid={uid}
                                initialBio={profileData?.bio || ""}
                                initialLocation={profileData?.location || ""}
                                onSave={handleProfileSave}
                            />
                        )}
                    </div>

                    {/* Posts Section */}
                    <div className="mb-4">
                        {/* Get Total posts with server aggregate here */}
                        <h2 className="text-lg font-semibold text-slate-700 mb-3 px-2">
                            Total Posts ({totalUserPosts})
                        </h2>

                        {posts.length === 0 && isOwner && (
                            <EmptyOwnerPosts />
                        )}

                        {posts.map((post) => (
                            <div key={post.id} className="mb-4">
                                <div className="flex justify-end px-2 mb-1">
                                    <Link
                                        to={`/p/${post.id}`}
                                        className="inline-flex items-center justify-center rounded p-1 text-slate-300 hover:text-slate-500 hover:bg-slate-100 transition-colors no-underline"
                                        aria-label="Open standalone post"
                                        title="Open standalone post"
                                    >
                                        <CopyIcon size={14} />
                                    </Link>
                                </div>
                                <SinglePost post={post} deletePost={deleteProfilePost} editPost={editProfilePost} />
                            </div>
                        ))}

                        {/* Load More Button */}
                        {hasMorePosts && posts.length > 0 && (
                            <div className="text-center py-4">
                                <button
                                    onClick={loadMorePosts}
                                    disabled={loadingMore}
                                    className="px-6 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    {loadingMore ? "Loading..." : "Load More Posts"}
                                </button>
                            </div>
                        )}

                        {!hasMorePosts && posts.length > 0 && (
                            <p className="text-center text-slate-400 text-sm py-4">No more posts</p>
                        )}
                    </div>
                </div>
            }
        </div>
    );


};

function getOwnerDisplayName(userDetails, user) {
    const fullName = `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`.trim();
    return fullName || user?.displayName || "Your Profile";
}

function EmptyOwnerPosts() {
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <span className="text-2xl leading-none">+</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Your profile is ready</h3>
            <p className="text-sm text-slate-500 mb-4">
                Add your first feed post and it will show up here for the community to see.
            </p>
            <Link
                to="/"
                className="inline-flex items-center justify-center rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white no-underline transition-colors hover:bg-blue-600 hover:no-underline"
            >
                Create a Post
            </Link>
        </div>
    );
}

function LightBoxModal({ src, alt }) {
    return <div
        className="fixed inset-0 z-50 bg-[#000] !bg-opacity-80 flex items-center justify-center p-4"
        data-close
    >
        {/* Close Button */}
        <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full text-white text-2xl font-bold transition-colors z-10"
            aria-label="Close"
            data-close
        >
            ×
        </button>

        {/* Full-size Profile Picture */}
        <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
        />
    </div>
}

/**
 * ProfilePostCard - Simplified post display for profile page 
 */
// function ProfilePostCard({ post, onPostClick }) {
//     return (
//         <div
//             onClick={onPostClick}
//             className="block bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-3 hover:shadow-md transition-shadow cursor-pointer"
//         >
//             <p className="text-slate-800 mb-2 line-clamp-3 whitespace-pre-line">{post.message}</p>

//             {/* Post images preview */}
//             {post.pictures && post.pictures.length > 0 && (
//                 <div className="flex gap-2 mb-2 overflow-hidden">
//                     {post.pictures.slice(0, 3).map((pic, idx) => (
//                         <img
//                             key={idx}
//                             src={pic}
//                             alt={`Post ${idx + 1}`}
//                             className="w-16 h-16 object-cover rounded-lg"
//                         />
//                     ))}
//                     {post.pictures.length > 3 && (
//                         <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500 text-sm">
//                             +{post.pictures.length - 3}
//                         </div>
//                     )}
//                 </div>
//             )}

//             {/* Post metadata */}
//             <div className="flex items-center gap-4 text-sm text-slate-500">
//                 <span>
//                     {new Date(post.epochTime).toLocaleDateString("en-US", {
//                         month: "short",
//                         day: "numeric",
//                         year: "numeric",
//                     })}
//                 </span>
//                 <span className="flex items-center gap-1">
//                     <HeartIcon size={16} style="stroke" color="currentcolor" />
//                     {post.count_likes || 0}
//                 </span>
//                 <span className="flex items-center gap-1">
//                     <TextBubbleIcon size={16} style="stroke" color="currentcolor" />
//                     {post.count_comments || 0}
//                 </span>
//             </div>
//         </div>
//     );
// }

export default SocialProfilePage;
